import { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import type { ClientProgramTarget } from '@/api/clientProgramTargets';
import type { TargetDataPoint } from '@/api/targetDataPoints';
import type { GraphConfig } from '@/api/targetGraphConfigs';
import { buildGraphData } from './graphData';

interface Props {
    target: Pick<ClientProgramTarget, 'dataType' | 'masteryValue' | 'name'>;
    points: TargetDataPoint[];
    config: GraphConfig;
}

export function TargetGraph({ target, points, config }: Props) {
    const data = useMemo(() => buildGraphData(points, config, target), [points, config, target]);

    if (data.points.length === 0) {
        return (
            <div className="h-[360px] flex items-center justify-center text-text-subtle text-sm">
                No data points in the selected range.
            </div>
        );
    }

    const Chart = config.chartType === 'bar' ? BarChart : LineChart;

    return (
        // min-w-0 so a grid/flex ancestor doesn't force this to its intrinsic
        // width, which would measure 0 on the first paint and trip Recharts'
        // "width(-1) and height(-1)" warning. Pixel height on the container
        // avoids the same issue on the vertical axis.
        <div className="w-full min-w-0">
            <ResponsiveContainer width="100%" height={360}>
                <Chart data={data.points} margin={{ top: 12, right: 20, left: 0, bottom: 6 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                        tickLine={false}
                        axisLine={{ stroke: 'var(--color-border)' }}
                    />
                    <YAxis
                        domain={data.yDomain as [number | string, number | string]}
                        tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }}
                        tickLine={false}
                        axisLine={{ stroke: 'var(--color-border)' }}
                        width={44}
                        tickFormatter={(v: number) => `${v}${data.unitSuffix}`}
                    />
                    <Tooltip
                        content={(p: unknown) => <GraphTooltip tip={p as TooltipLike} unit={data.unitSuffix} />}
                    />

                    {data.criterionValue !== null && (
                        <ReferenceLine
                            y={data.criterionValue}
                            stroke="var(--color-accent-dark)"
                            strokeDasharray="4 2"
                            label={{
                                value: `Mastery ${data.criterionValue}${data.unitSuffix}`,
                                position: 'insideTopRight',
                                fill: 'var(--color-accent-dark)',
                                fontSize: 10,
                            }}
                        />
                    )}

                    {config.chartType === 'bar' ? (
                        <Bar dataKey="value" fill="var(--color-accent)" radius={[2, 2, 0, 0]} />
                    ) : (
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke="var(--color-text)"
                            strokeWidth={2}
                            dot={{ r: 3, fill: 'var(--color-text)' }}
                            activeDot={{ r: 4 }}
                        />
                    )}

                    {config.showTrendline && (
                        <Line
                            type="linear"
                            dataKey="trend"
                            stroke="var(--color-text-subtle)"
                            strokeDasharray="5 3"
                            strokeWidth={1.5}
                            dot={false}
                            isAnimationActive={false}
                        />
                    )}
                </Chart>
            </ResponsiveContainer>
        </div>
    );
}

// Recharts' Tooltip content signature is version-churn-y; we only consume
// the three fields we care about so a loose shape is simpler than chasing
// the typed prop.
interface TooltipLike {
    active?: boolean;
    label?: string | number;
    payload?: Array<{ payload?: { value: number; count: number; trend?: number } }>;
}

function GraphTooltip({ tip, unit }: { tip: TooltipLike; unit: string }) {
    if (!tip?.active || !tip.payload || tip.payload.length === 0) return null;
    const row = tip.payload[0].payload;
    if (!row) return null;
    return (
        <div className="bg-surface border border-border rounded-md shadow-md px-2.5 py-1.5 text-xs">
            <div className="font-medium text-text">{tip.label}</div>
            <div className="text-text-muted">Value: <span className="font-semibold text-text">{row.value}{unit}</span></div>
            <div className="text-text-subtle text-2xs">{row.count} {row.count === 1 ? 'point' : 'points'}</div>
            {typeof row.trend === 'number' && (
                <div className="text-text-subtle text-2xs">Trend: {row.trend}{unit}</div>
            )}
        </div>
    );
}
